/* ===========================================================
   ui/ui.jsx — buttons, icons, dialogs, menus, toasts

   Everything keyboard-reachable with visible focus, real <button>s,
   labels for screen readers, Esc to close, focus kept inside an open
   dialog and handed back when it closes.
   =========================================================== */

import { signal } from '@preact/signals';
import { useEffect, useRef, useState, useLayoutEffect } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { ICONS } from '../lib/icons.js';
import { Proto, translate } from './proto.jsx';

export function Icon({ name, size = 18, class: cls = '' }) {
  const paths = ICONS[name] || [];
  return (
    <svg class={`icon ${cls}`} viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      {paths.map(d => <path d={d} key={d} />)}
    </svg>
  );
}

export function Button({ icon, children, kind = 'plain', size, class: cls = '', ...rest }) {
  return (
    <button type="button" class={`btn ${kind} ${size || ''} ${cls}`} {...rest}>
      {icon && <Icon name={icon} size={size === 'sm' ? 15 : 17} />}
      {children && <span>{children}</span>}
    </button>
  );
}

/** Icon-only button: always has a label for screen readers and a tooltip. */
export function IconButton({ icon, label, active, class: cls = '', size = 18, ...rest }) {
  return (
    <button type="button" class={`iconbtn ${active ? 'on' : ''} ${cls}`} aria-label={label} title={label}
      aria-pressed={active === undefined ? undefined : String(!!active)} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

export function Spinner({ label = 'Loading' }) {
  return <span class="spinner" role="status" aria-label={label} />;
}

/* ---------- dialogs ---------- */

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function Dialog({ title, onClose, children, wide, full, actions, label, class: cls = '' }) {
  const ref = useRef();
  useEffect(() => {
    const before = document.activeElement;
    const el = ref.current;
    const first = el.querySelector('[autofocus]') || el.querySelector(FOCUSABLE);
    first?.focus();
    const onKey = e => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); }
      if (e.key === 'Tab') {
        const items = [...el.querySelectorAll(FOCUSABLE)].filter(x => x.offsetParent !== null);
        if (!items.length) return;
        const [a, b] = [items[0], items[items.length - 1]];
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); b.focus(); }
        else if (!e.shiftKey && document.activeElement === b) { e.preventDefault(); a.focus(); }
      }
    };
    el.addEventListener('keydown', onKey);
    document.body.classList.add('has-dialog');
    return () => {
      el.removeEventListener('keydown', onKey);
      document.body.classList.remove('has-dialog');
      before?.focus?.();
    };
  }, []);
  return createPortal(
    <div class="scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div ref={ref} class={`dialog ${wide ? 'wide' : ''} ${full ? 'full' : ''} ${cls}`} role="dialog" aria-modal="true" aria-label={label || title}>
        {title && (
          <header class="dialog-head">
            <h2>{title}</h2>
            {onClose && <IconButton icon="close" label="Close" onClick={onClose} />}
          </header>
        )}
        <div class="dialog-body">{children}</div>
        {actions && <footer class="dialog-foot">{actions}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/** A simple ask-for-text dialog. Resolves to the text, or null. */
export function promptDialog(opts) { return openModal(done => <PromptBox {...opts} done={done} />); }
export function confirmDialog(opts) { return openModal(done => <ConfirmBox {...opts} done={done} />); }

function PromptBox({ title, label, value = '', placeholder, ok = 'Save', hint, done }) {
  const [v, setV] = useState(value);
  return (
    <Dialog title={title} onClose={() => done(null)}
      actions={<><Button onClick={() => done(null)}>Cancel</Button><Button kind="primary" onClick={() => done(v)}>{ok}</Button></>}>
      <form onSubmit={e => { e.preventDefault(); done(v); }}>
        <label class="field">
          <span>{label}</span>
          <input autofocus value={v} placeholder={placeholder} maxLength={200} onInput={e => setV(e.target.value)} />
        </label>
        {hint && <p class="hint">{hint}</p>}
      </form>
    </Dialog>
  );
}

function ConfirmBox({ title, text, ok = 'OK', danger, cancel = 'Cancel', done }) {
  return (
    <Dialog title={title} onClose={() => done(false)}
      actions={<><Button onClick={() => done(false)}>{cancel}</Button><Button kind={danger ? 'danger' : 'primary'} autofocus onClick={() => done(true)}>{ok}</Button></>}>
      <p>{text}</p>
    </Dialog>
  );
}

export const modals = signal([]);
export function openModal(render) {
  return new Promise(resolve => {
    const id = Math.random();
    const done = v => { modals.value = modals.value.filter(m => m.id !== id); resolve(v); };
    modals.value = [...modals.value, { id, node: render(done) }];
  });
}
export function Modals() {
  return <>{modals.value.map(m => <div key={m.id}>{m.node}</div>)}</>;
}

/* ---------- menus ---------- */

/**
 * <Menu button={<IconButton …/>} items={[{ label, icon, onSelect, hint, danger } | 'sep']} />
 * Arrow keys move, Enter picks, Esc closes.
 */
export function Menu({ trigger, items, align = 'left', label }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef();
  const close = (refocus = false) => { setOpen(false); if (refocus) wrap.current?.querySelector('button')?.focus(); };
  return (
    <div class="menu-wrap" ref={wrap}>
      <span onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={String(open)}>{trigger}</span>
      {open && <FloatingMenu items={items} label={label} align={align} anchor={() => wrap.current?.getBoundingClientRect()}
        owner={wrap} onClose={close} />}
    </div>
  );
}

/* The list itself, floating above the page (a portal on <body>), so a
   scrolling toolbar or a list with hidden overflow can't clip it — that
   was the "menu problems": Insert opened inside the toolbar and showed
   in a 48 px strip. Placed under its button (or at the mouse for the
   right-click menu), flipped up / left when there's no room, kept on
   screen, closed by a tap outside, scrolling, resizing or Esc. */
export function FloatingMenu({ items, label, align = 'left', anchor, at, owner, onClose }) {
  const list = useRef();
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const m = list.current;
    if (!m) return;
    const vw = window.innerWidth, vh = window.innerHeight, pad = 8;
    const w = m.offsetWidth, h = m.offsetHeight;
    let left, top, maxH;
    if (at) {
      left = at.x + w > vw - pad ? at.x - w : at.x;
      top = at.y + h > vh - pad ? Math.max(pad, at.y - h) : at.y;
      maxH = vh - pad * 2;
    } else {
      const r = anchor?.() || { left: 0, right: 0, top: 0, bottom: 0 };
      left = align === 'right' ? r.right - w : r.left;
      const below = vh - r.bottom - pad, above = r.top - pad;
      const up = h > below && above > below;
      maxH = Math.max(160, (up ? above : below) - 6);
      top = up ? Math.max(pad, r.top - 6 - Math.min(h, maxH)) : r.bottom + 6;
    }
    left = Math.max(pad, Math.min(left, vw - w - pad));
    setPos({ left, top, maxHeight: maxH });
  }, []);
  // focus the first item once it's visible (a hidden element can't take focus)
  useEffect(() => { if (pos) list.current?.querySelector('[role=menuitem]:not(:disabled)')?.focus({ preventScroll: true }); }, [!!pos]);
  useEffect(() => {
    const outside = e => { if (!list.current?.contains(e.target) && !owner?.current?.contains(e.target)) onClose(); };
    const scrolled = e => { if (!list.current?.contains(e.target)) onClose(); };
    const resized = () => onClose();
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('scroll', scrolled, true);
    window.addEventListener('resize', resized);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('scroll', scrolled, true);
      window.removeEventListener('resize', resized);
    };
  }, []);
  const onKey = e => {
    const els = [...list.current.querySelectorAll('[role=menuitem]:not(:disabled)')];
    const i = els.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); els[(i + 1) % els.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); els[(i - 1 + els.length) % els.length]?.focus(); }
    if (e.key === 'Home') { e.preventDefault(); els[0]?.focus(); }
    if (e.key === 'End') { e.preventDefault(); els[els.length - 1]?.focus(); }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true); }
    if (e.key === 'Tab') onClose();
  };
  return createPortal(
    <div class="menu floating" role="menu" aria-label={label} ref={list} onKeyDown={onKey}
      style={pos ? { left: `${pos.left}px`, top: `${pos.top}px`, maxHeight: `${pos.maxHeight}px` } : { visibility: 'hidden', left: 0, top: 0 }}
      onContextMenu={e => e.preventDefault()}>
      {items.filter(Boolean).map((it, i) => (it === 'sep'
        ? <div class="menu-sep" role="separator" key={`s${i}`} />
        : it.heading ? <div class="menu-head" key={`h${i}`}>{it.heading}</div>
          : (
            <button type="button" role="menuitem" key={it.label} class={`menu-item ${it.danger ? 'danger' : ''} ${it.active ? 'on' : ''}`}
              disabled={it.disabled} onClick={() => { onClose(); it.onSelect?.(); }}>
              {it.icon ? <Icon name={it.icon} size={16} /> : <span class="menu-noicon" />}
              <span class="menu-label">{it.label}</span>
              {it.hint && <span class="menu-hint">{it.hint}</span>}
            </button>
          )))}
    </div>,
    document.body);
}

/* ---------- toasts ---------- */

export const toasts = signal([]);
let toastId = 0;

/**
 * toast('Saved') · toast(err, { kind: 'error' }) — errors are translated and
 * shown by Proto (wrench when it's our bug).
 */
export function toast(msg, { kind = 'info', ms, action, proto } = {}) {
  const id = ++toastId;
  let text = typeof msg === 'string' ? msg : msg?.message || String(msg);
  let fixing = false, code = '';
  if (kind === 'error') {
    const t = translate(msg);
    text = t.text; fixing = t.ours; code = t.code;
  }
  const item = { id, text, kind, fixing, code, action, proto: proto ?? kind === 'error' };
  toasts.value = [...toasts.value.slice(-3), item];
  setTimeout(() => dismiss(id), ms ?? (kind === 'error' ? 8000 : action ? 9000 : 3500));
  return id;
}
export function dismiss(id) { toasts.value = toasts.value.filter(t => t.id !== id); }

export function Toaster() {
  return (
    <div class="toasts" role="status" aria-live="polite">
      {toasts.value.map(t => (
        <div class={`toast ${t.kind}`} key={t.id}>
          {t.proto && <Proto size={40} fixing={t.fixing} />}
          <div class="toast-text">
            <span>{t.text}</span>
            {t.code && t.kind === 'error' && <small class="fine">{String(t.code).slice(0, 40)}</small>}
          </div>
          {t.action && <button type="button" class="toast-action" onClick={() => { dismiss(t.id); t.action.run(); }}>{t.action.label}</button>}
          <button type="button" class="toast-x" aria-label="Dismiss" onClick={() => dismiss(t.id)}><Icon name="close" size={14} /></button>
        </div>
      ))}
    </div>
  );
}

/* ---------- small helpers ---------- */

export function useMedia(query) {
  const [m, setM] = useState(() => matchMedia(query).matches);
  useLayoutEffect(() => {
    const mq = matchMedia(query);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return m;
}

export function timeAgo(t) {
  if (!t) return '';
  const s = (Date.now() - t) / 1000;
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400 && new Date(t).getDate() === new Date().getDate()) return new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (s < 86400 * 6) return new Date(t).toLocaleDateString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  return new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric', year: new Date(t).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
}

export function bytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** A person's colour for cursors and avatars, stable from their id/name. */
export function colourFor(seed) {
  let h = 0;
  for (const c of String(seed || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  // hex (the live-cursor library only understands hex colours); all readable with white text
  const palette = ['#c2410c', '#b45309', '#a16207', '#15803d', '#0f766e', '#0369a1', '#1d4ed8', '#6d28d9', '#a21caf', '#be123c'];
  return palette[h % palette.length];
}

export function Avatar({ name, seed, size = 28 }) {
  const initials = String(name || '?').split(/\s+/).map(s => s[0]).join('').slice(0, 2).toUpperCase();
  return <span class="avatar" style={{ background: colourFor(seed || name), width: `${size}px`, height: `${size}px`, fontSize: `${size * 0.42}px` }} title={name} aria-label={name}>{initials}</span>;
}
